import { action, makeObservable, observable } from 'mobx';
import { ApplicationUuid } from '@bringg/types';
import { getSharedRootStore } from '@bringg-frontend/global-stores';
import { getRoot } from 'mobx-easy';

import RootStore from 'bringg-web/stores/root-store';
import { ReserveRow } from '../types';
import { isReserveRow } from '../helpers';

export class ReserveCapacityStore {
	data: ReserveRow[] = [];
	isLoading = false;
	isSaving = false;
	fetchFailed = false;
	saveFailed = false;
	teamId: number | null = null;

	constructor(teamId?: number) {
		makeObservable(this, {
			data: observable.shallow,
			isLoading: observable,
			isSaving: observable,
			fetchFailed: observable,
			saveFailed: observable,
			teamId: observable,
			fetchConfig: action,
			saveConfig: action,
			resetSaveFailed: action
		});

		this.teamId = teamId ?? null;
	}

	get isTeamConfig(): boolean {
		return Number.isFinite(this.teamId);
	}

	resetSaveFailed = (): void => {
		this.saveFailed = false;
	};

	fetchConfig = async (): Promise<void> => {
		this.isLoading = true;
		this.fetchFailed = false;

		try {
			const config = this.isTeamConfig ? await this.fetchTeamConfig() : await this.fetchMerchantConfig();

			const reserved_capacity = config?.reserved_capacity;

			if (reserved_capacity === undefined || reserved_capacity === null) {
				this.data = [];
				return;
			}

			if (!Array.isArray(reserved_capacity) || !reserved_capacity.every(isReserveRow)) {
				throw new Error('reserved_capacity property has unexpected shape');
			}

			this.data = reserved_capacity;
		} catch (error) {
			console.error('Failed to fetch reserve capacity config', error);
			this.fetchFailed = true;
		} finally {
			this.isLoading = false;
		}
	};

	saveConfig = async (rows: ReserveRow[]): Promise<boolean> => {
		this.isSaving = true;
		this.saveFailed = false;

		try {
			if (this.isTeamConfig) {
				await this.saveTeamConfig(rows);
			} else {
				await this.saveMerchantConfig(rows);
			}

			this.data = rows;
			return true;
		} catch (error) {
			console.error('Failed to save reserve capacity config', error);
			this.saveFailed = true;
			return false;
		} finally {
			this.isSaving = false;
		}
	};

	private fetchTeamConfig = async (): Promise<Record<'reserved_capacity', ReserveRow[]>> => {
		const { applicationTeamConfigurationStore } = getRoot<RootStore>().data;
		return applicationTeamConfigurationStore.getConfiguration(ApplicationUuid.Availability, this.teamId);
	};

	// eslint-disable-next-line class-methods-use-this
	private fetchMerchantConfig = async (): Promise<Record<'reserved_capacity', ReserveRow[]>> => {
		const { applicationMerchantConfigurationStore } = getSharedRootStore().data;
		return applicationMerchantConfigurationStore.fetchConfiguration(ApplicationUuid.Availability);
	};

	private saveTeamConfig = async (rows: ReserveRow[]): Promise<void> => {
		const { applicationTeamConfigurationStore } = getRoot<RootStore>().data;
		const existingConfig =
			(await applicationTeamConfigurationStore.getConfiguration(ApplicationUuid.Availability, this.teamId)) ?? {};

		await applicationTeamConfigurationStore.updateConfiguration(ApplicationUuid.Availability, this.teamId, {
			...existingConfig,
			reserved_capacity: rows
		});
	};

	// eslint-disable-next-line class-methods-use-this
	private saveMerchantConfig = async (rows: ReserveRow[]): Promise<void> => {
		const { applicationMerchantConfigurationStore } = getSharedRootStore().data;
		const existingConfig =
			applicationMerchantConfigurationStore.getConfiguration(ApplicationUuid.Availability) ?? {};

		await applicationMerchantConfigurationStore.updateConfiguration(ApplicationUuid.Availability, {
			...existingConfig,
			reserved_capacity: rows
		});
	};
}
